// frontend/src/components/ClientTable.js

import React, { useEffect, useState } from 'react';
import {
    Box,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Typography,
    Button,
    LinearProgress,
    Tooltip,
    Chip,
    Tabs,
    Tab,
    CircularProgress,
    Stack,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutline from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme, styled, alpha } from '@mui/material/styles';
import { getColorByPercentage, getDispatchColor, getChipStyles } from '../utils/colorUtils';
import dayjs from 'dayjs';

const ACTIVE_ISSUE_STATUSES = ['ON HOLD', 'IN PROGRESS', 'URGENT', 'ERROR'];

// Blinking warning icon
const BlinkingWarningIcon = styled(WarningIcon)(({ theme }) => ({
    color: theme.palette.error.main,
    animation: 'blinker 1s linear infinite',
    '@keyframes blinker': {
        '50%': { opacity: 0 },
    },
    marginLeft: theme.spacing(0.5),
}));

const categoryColors = {
    'Giant Cards': '#dc004e',
    'Wrapping Paper': '#ff9800',
    'GiftOnly': '#20c997',
    'Personlaised':'#e72d88',
    'Default': '#1976d2',
    'CARD ONLY': '#dc004e',
    'NO CARD': '#ff9800',
    'CARD WITH BOTTLE': '#20c997',
};
const defaultCategoryColor = '#e72d88';

const getCategoryChipStyles = (categoryName) => ({
    backgroundColor: categoryColors[categoryName] || defaultCategoryColor,
    color: '#fff',
    fontWeight: 500,
    height: 24,
});

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  // Use .replace('Z', '') if needed
  const d = dayjs(dateString.replace('Z', ''));
  if (!d.isValid()) return 'Invalid Date';
  return d.format('YYYY-MM-DD HH:mm:ss');
};

const hasActiveIssue = (status) => ACTIVE_ISSUE_STATUSES.includes(String(status || '').trim().toUpperCase());

const getIssuePreview = (comments = '') => {
    const trimmed = String(comments || '').trim();
    if (!trimmed) return '';
    return trimmed.length > 80 ? `${trimmed.slice(0, 80)}...` : trimmed;
};

const ClientTable = ({
    // For stats table
    clients = [],
    onDispatchTodayClick,
    onReShipOrdersClick,         // <-- passed from Dashboard, callback(clientName)
    onCutoffTimeClick,
    // For modals (inside dialog)
    orders = [],
    categories = [],
    loading,
    error,
    tabIndex,
    setTabIndex,
    dialogMode, // "dispatch" or "reship"
    openIssueDialog,
    openCancelDialog,
    openDispatchedDialog,
    currentClientName,
    reloadOrders,
}) => {
    const theme = useTheme();
    const dispatchPalette = theme.palette.dispatch;
    const [issueFilter, setIssueFilter] = useState('all');

    useEffect(() => {
        setIssueFilter('all');
    }, [dialogMode, currentClientName, tabIndex]);

    const getDialogActionButtonSx = (paletteKey, variant = 'soft') => {
        const paletteColor = theme.palette[paletteKey]?.main || theme.palette.grey[700];
        const isFilled = variant === 'filled';
        const isGhost = variant === 'ghost';

        return {
            width: 32,
            height: 32,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: isGhost ? alpha(paletteColor, 0.14) : alpha(paletteColor, isFilled ? 0.18 : 0.2),
            color: isFilled ? theme.palette.common.white : paletteColor,
            backgroundColor: isFilled
                ? paletteColor
                : isGhost
                    ? alpha(paletteColor, 0.02)
                    : alpha(paletteColor, 0.05),
            '&:hover': {
                backgroundColor: isFilled
                    ? paletteColor
                    : isGhost
                        ? alpha(paletteColor, 0.07)
                        : alpha(paletteColor, 0.1),
                borderColor: alpha(paletteColor, isGhost ? 0.2 : 0.26),
            },
        };
    };

    // --- RENDER: Orders Table (used in modal for dispatch/reship) ---
    if (dialogMode === "dispatch" || dialogMode === "reship") {
        if (loading) return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
        if (error) return (
            <Typography color="error" sx={{ p: 2 }}>
                {error}
            </Typography>
        );
        if (!orders || orders.length === 0) return (
            <Typography sx={{ p: 2 }}>
                No orders found for this client.
            </Typography>
        );

        const activeCategory = categories && categories.length > 0
            ? categories[tabIndex] ?? categories[0]
            : null;
        const categoryOrders = activeCategory
            ? orders.filter((order) => (order.category ?? 'Uncategorised') === activeCategory)
            : orders;
        const activeIssueCount = categoryOrders.filter((order) => hasActiveIssue(order.issueStatus)).length;
        const noIssueCount = Math.max(categoryOrders.length - activeIssueCount, 0);
        const filteredOrders = categoryOrders.filter((order) => {
            if (issueFilter === 'active') return hasActiveIssue(order.issueStatus);
            if (issueFilter === 'clear') return !hasActiveIssue(order.issueStatus);
            return true;
        });

        return (
            <Box>
                {categories && categories.length > 1 && (
                    <Tabs
                        value={tabIndex}
                        onChange={(_, idx) => setTabIndex(idx)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                        {categories.map(cat => {
                            const cnt = orders.filter(o => (o.category ?? 'Uncategorised') === cat).length;
                            return (
                                <Tab key={cat} label={`${cat} (${cnt})`} />
                            );
                        })}
                    </Tabs>
                )}
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ md: 'center' }}
                    >
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Chip
                                label={`${filteredOrders.length} shown`}
                                size="small"
                                color="primary"
                                variant="filled"
                            />
                            <Chip
                                label={`Issue rows: ${activeIssueCount}`}
                                size="small"
                                color="warning"
                                variant={activeIssueCount > 0 ? 'filled' : 'outlined'}
                            />
                            <Chip
                                label={`Clear rows: ${noIssueCount}`}
                                size="small"
                                variant="outlined"
                            />
                        </Stack>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Chip
                                label="All"
                                size="small"
                                clickable
                                color={issueFilter === 'all' ? 'primary' : 'default'}
                                variant={issueFilter === 'all' ? 'filled' : 'outlined'}
                                onClick={() => setIssueFilter('all')}
                            />
                            <Chip
                                label="Active issues"
                                size="small"
                                clickable
                                color={issueFilter === 'active' ? 'warning' : 'default'}
                                variant={issueFilter === 'active' ? 'filled' : 'outlined'}
                                onClick={() => setIssueFilter('active')}
                            />
                            <Chip
                                label="No issue"
                                size="small"
                                clickable
                                color={issueFilter === 'clear' ? 'success' : 'default'}
                                variant={issueFilter === 'clear' ? 'filled' : 'outlined'}
                                onClick={() => setIssueFilter('clear')}
                            />
                        </Stack>
                    </Stack>

                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 2, maxHeight: { xs: '55vh', md: '62vh' } }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Order Number</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Create Date</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Shipping Carrier</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Shipping Method</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Issue / Notes</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f8fafc' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredOrders.map(order => (
                                    <TableRow
                                        key={order.orderId}
                                        sx={hasActiveIssue(order.issueStatus) ? {
                                            backgroundColor: alpha(theme.palette.warning.main, 0.06),
                                        } : undefined}
                                    >
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {order.orderNumber}
                                                </Typography>
                                                {order.category && (
                                                    <Box>
                                                        <Chip
                                                            label={order.category}
                                                            size="small"
                                                            sx={getCategoryChipStyles(order.category)}
                                                        />
                                                    </Box>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                                        <TableCell>{order.shippingCarrier || order.shipping_carrier}</TableCell>
                                        <TableCell>{order.shippingMethod || order.shipping_method}</TableCell>
                                        <TableCell>
                                            {order.issueStatus ? (
                                                <Stack spacing={0.75} sx={{ minWidth: 220, maxWidth: 320 }}>
                                                    <Box>
                                                        <Chip
                                                            label={order.issueStatus}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getChipStyles(order.issueStatus),
                                                                color: '#fff',
                                                            }}
                                                        />
                                                    </Box>
                                                    {order.comments ? (
                                                        <Tooltip title={order.comments}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    lineHeight: 1.35,
                                                                }}
                                                            >
                                                                {getIssuePreview(order.comments)}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            No issue note logged
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    No issue logged
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Confirm dispatch">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Confirm dispatch for order ${order.orderNumber}`}
                                                        onClick={() => openDispatchedDialog(order)}
                                                        sx={getDialogActionButtonSx('secondary', 'filled')}
                                                    >
                                                        <LocalShippingIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title={hasActiveIssue(order.issueStatus) ? 'Update order issue' : 'Add order issue'}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Add or update issue for order ${order.orderNumber}`}
                                                        onClick={() => openIssueDialog(order)}
                                                        sx={getDialogActionButtonSx(
                                                            hasActiveIssue(order.issueStatus) ? 'warning' : 'primary',
                                                            hasActiveIssue(order.issueStatus) ? 'soft' : 'ghost'
                                                        )}
                                                    >
                                                        <ErrorOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {currentClientName === "Funky Pigeon" && order.category === "GiftOnly" && (
                                                    <Tooltip title="Confirm cancellation">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Cancel order ${order.orderNumber}`}
                                                            onClick={() => openCancelDialog(order)}
                                                            sx={getDialogActionButtonSx('error', 'ghost')}
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredOrders.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                                No orders match the current issue filter for this queue.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Box>
        );
    }

    // --- RENDER: Main Clients Statistics Table ---
    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
                <TableHead>
                    <TableRow>
                        {/* Cutoff Times Icon */}
                        <TableCell>
                            <Tooltip title="Cutoff Times">
                                <AlarmIcon />
                            </Tooltip>
                        </TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            New Orders
                        </TableCell>
                        <TableCell>Dispatch Queue</TableCell>
                        <TableCell>Issue Watchlist</TableCell>
                        <TableCell>Re-Ship Queue</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            Dispatched
                        </TableCell>
                        <TableCell>Cancelled</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            Accuracy
                        </TableCell>
                        <TableCell>Queue Progress</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {clients.map((client) => {
                        const displayProgress =
                            (client.requiredDispatchToday === 0 && client.dispatchedOrders === 0 && client.reshipOrdersToday === 0)
                                ? 100
                                : (client.progressPercentage > 100 ? 100 : client.progressPercentage || 0);
                        const issueOrdersToday = Number(client.issueOrdersToday || 0);
                        const needsAttention = issueOrdersToday > 0 || Number(client.reshipOrdersToday || 0) > 0;

                        const dynamicColor = getColorByPercentage(displayProgress, dispatchPalette);
                        const dispatchColor = getDispatchColor(client.requiredDispatchToday, dispatchPalette);
                        const reshipColor = getDispatchColor(client.reshipOrdersToday, dispatchPalette);
                        return (
                            <TableRow
                                key={client.clientName}
                                sx={needsAttention ? { backgroundColor: 'rgba(255, 152, 0, 0.035)' } : undefined}
                            >
                                {/* Cutoff Times Icon/Button */}
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => onCutoffTimeClick(e, client)}
                                        aria-label={`View cutoff times for ${client.clientName}`}
                                    >
                                        <AlarmIcon color="action" />
                                    </IconButton>
                                </TableCell>
                                <TableCell>{client.clientName}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    {client.totalNewOrders || 0}
                                </TableCell>
                                {/* Required Dispatch Today */}
                                <TableCell>
                                    {client.requiredDispatchByCategory &&
                                        Object.values(client.requiredDispatchByCategory).some(v => v > 0) ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {Object.entries(client.requiredDispatchByCategory)
                                                .filter(([_, count]) => count > 0)
                                                .map(([categoryName, count]) => (
                                                    <Chip
                                                        key={categoryName}
                                                        label={`${categoryName}: ${count}`}
                                                        size="small"
                                                        sx={getCategoryChipStyles(categoryName)}
                                                    />
                                                ))}
                                            <IconButton
                                                size="small"
                                                onClick={() => onDispatchTodayClick(client.clientName)}
                                                aria-label={`View Required Dispatch Today Orders for ${client.clientName}`}
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            onClick={() => onDispatchTodayClick(client.clientName)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                                color: dispatchColor,
                                                padding: 0,
                                                minWidth: 'auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                            aria-label={`View Required Dispatch Today Orders for ${client.clientName}`}
                                        >
                                            {client.requiredDispatchToday || 0}
                                            <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
                                        </Button>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {issueOrdersToday > 0 ? (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Chip
                                                label={`${issueOrdersToday} issue${issueOrdersToday === 1 ? '' : 's'}`}
                                                size="small"
                                                color="warning"
                                                variant="filled"
                                            />
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Clear
                                        </Typography>
                                    )}
                                </TableCell>
                                {/* Re-Ship Orders Today */}
                                <TableCell>
                                    {client.reshipOrdersToday > 0 ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: reshipColor,
                                                }}
                                                aria-label={`Re-Ship Orders Today: ${client.reshipOrdersToday}`}
                                            >
                                                {client.reshipOrdersToday}
                                            </Typography>
                                            <BlinkingWarningIcon aria-label={`Re-Ship Orders Today: ${client.reshipOrdersToday}`} />
                                            <Button
                                                variant="text"
                                                color="error"
                                                size="small"
                                                onClick={() => onReShipOrdersClick(client.clientName)}
                                                sx={{ ml: 1 }}
                                                aria-label={`View Re-Ship Orders Today for ${client.clientName}`}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            0
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <Typography sx={{ color: dispatchPalette.green, fontWeight: 'bold' }}>
                                        {client.dispatchedOrders || 0}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: client.cancelledOrdersToday > 0
                                            ? dispatchPalette.red
                                            : dispatchPalette.green,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {client.cancelledOrdersToday || 0}
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: client.fulfilmentAccuracyRate >= 90
                                                ? 'success.main'
                                                : client.fulfilmentAccuracyRate >= 75
                                                    ? 'warning.main'
                                                    : 'error.main',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {client.fulfilmentAccuracyRate
                                            ? `${client.fulfilmentAccuracyRate.toFixed(2)}%`
                                            : '0.00%'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 'bold',
                                                mr: 1,
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}
                                        >
                                            {displayProgress}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={displayProgress}
                                            sx={{
                                                flexGrow: 1,
                                                height: 8,
                                                borderRadius: 5,
                                                [`& .MuiLinearProgress-bar`]: {
                                                    backgroundColor: dynamicColor,
                                                    transition: 'background-color 0.3s ease',
                                                },
                                                transition: 'background-color 0.3s ease',
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ClientTable;
